/**
 * Did the viewer iframe fail to start? Pure decision, so it runs under node:test; PlayerScreen
 * probes the frame and feeds the result in. A slow load is never a failure: only a frame that
 * cannot be reached (Chrome's error page is a cross-origin document) or that is not the viewer.
 */

/** What the Player sees in the frame right now. */
export type ViewerProbe =
  | "settled" // `walkbake.ready` or `walkbake.error`: the viewer is done, or reported its own error
  | "running" // `window.walkbake` exists but is not ready: a heavy walk loading, however long it takes
  | "starting" // the viewer page is there but its script has not created `walkbake` yet
  | "absent"; // unreachable (error page, cross-origin) or some other page (blank, a 404 page)

export type LoadVerdict = "settled" | "wait" | "failed";

/** An unreachable frame this long after its `load` event is a failed viewer. */
export const ABSENT_AFTER_LOAD_MS = 3000;
/** A frame that never fires `load` is a failed viewer after this long (when it is not the viewer either). */
export const NEVER_LOADED_MS = 20000;
/** The viewer page loaded but its script never created `walkbake`: the script failed to run. */
export const STARTING_AFTER_LOAD_MS = 20000;
/** The viewer page is parsing but `load` has not fired (many modules on a slow device): generous. */
export const STARTING_NEVER_LOADED_MS = 60000;

/** `sinceLoadMs` is null until the frame's `load` event has fired. */
export function judgeLoad(probe: ViewerProbe, sinceStartMs: number, sinceLoadMs: number | null): LoadVerdict {
  if (probe === "settled") return "settled";
  if (probe === "running") return "wait";
  if (probe === "absent") {
    const failed = sinceLoadMs === null ? sinceStartMs >= NEVER_LOADED_MS : sinceLoadMs >= ABSENT_AFTER_LOAD_MS;
    return failed ? "failed" : "wait";
  }
  const failed = sinceLoadMs === null ? sinceStartMs >= STARTING_NEVER_LOADED_MS : sinceLoadMs >= STARTING_AFTER_LOAD_MS;
  return failed ? "failed" : "wait";
}

export const VIEWER_FAILED_MESSAGE = (name: string): string =>
  `The walk viewer could not start (${name}). If it keeps failing, use Check for updates on this page.`;
